import { useState, useEffect, useContext } from 'react';
import { useLocation, RouterContext, goTo } from 't-lib-ui/router';
import { Spinner } from 't-lib-ui/widgets';
import { sanitizeNoWhitespace } from 't-lib-ui/connector';
import { AppState } from '../context/general';
import { getPublicFeedByUrl } from '../connector/api';
import { markAnonFromShare, applyFirstFeedDefaults } from '../connector/anon-onboarding';
import ShareHeader from '../components/ShareHeader';
import LegalFooter from '../components/LegalFooter';
import { findFollowedFeedByUrl } from './viewFeedUrl';
import './SharePage.css';

// /view?url=<feed url>: opens a feed by its RSS/Atom URL in the reader, by resolving it to its id
// and replacing the location with /a/feed/{id}. Followed feeds resolve locally without a request;
// everything else goes through the public by-URL lookup, which only finds EXISTING public feeds
// (never fetches or creates). When nothing is found, logged-in users continue in the add flow
// (which can create the feed); everyone else gets an explanation.
//
// /add?url=<feed url> is the same page with `follow`: a feed that isn't followed yet is followed
// (anonymously too — the anon engine keeps it in localStorage) before the reader opens.
const ViewFeedByUrlPage: React.FC<{ follow?: boolean }> = ({ follow }) => {
  const location = useLocation();
  const url = location.searchParams.get('url') ?? '';
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const { feedEngine } = appState;
  const isLoggedIn = appState.state.loginState == 'ok';
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!url) return setError('No feed URL was provided.');
    const followed = findFollowedFeedByUrl(feedEngine.getFeeds(), url);
    if (followed) return void goTo(routerCtx, `/a/feed/${followed.id}`, 'replace');

    let cancelled = false;
    getPublicFeedByUrl(url, appState.state.authData).then(async feed => {
      if (cancelled) return;
      // Prime the engine so the reader can render the (unfollowed) feed right away.
      feedEngine.registerFeedInfo(feed);
      // Following is best-effort: if it fails (e.g. feed quota), the feed still opens unfollowed. The
      // url may be an alias of a feed that is already followed, so check the resolved id too.
      if (follow && !feedEngine.getFeeds().some(f => f.id == feed.id))
        await feedEngine.subscribe(feed.id).then(() => {
          applyFirstFeedDefaults(feedEngine, feed.id);
          if (!isLoggedIn) markAnonFromShare();
        }, (e: unknown) => console.error('Following feed failed:', e));
      if (!cancelled) goTo(routerCtx, `/a/feed/${feed.id}`, 'replace');
    }).catch((e: unknown) => {
      if (cancelled) return;
      if (isLoggedIn) return void goTo(routerCtx, `/a/add?url=${encodeURIComponent(url)}`, 'replace');
      setError((e as Error).message);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, follow, isLoggedIn, feedEngine, routerCtx]);

  const displayUrl = sanitizeNoWhitespace(url, 256);

  return (
    <div className="sharePage">
      <ShareHeader />
      <div className="sharePageContent">
        {!error && (
          <div className="sharePageLoading">
            <Spinner spinning />
            <span>Looking up feed...</span>
          </div>
        )}

        {error && (
          <div className="sharePageError">
            <h2>This feed isn't on Downread yet</h2>
            {displayUrl && <p className="shareErrorUrl">{displayUrl}</p>}
            <p>
              We can only open feeds here that a Downread user already subscribes to.
              Sign up or log in to add this feed to your personal reader.
            </p>
            <div className="shareErrorActions">
              <a href="/signup" className="shareBannerCta">Sign up free</a>
              <a href="/login" className="shareBannerCtaSecondary">Log in</a>
            </div>
          </div>
        )}
        <LegalFooter bottom />
      </div>
    </div>
  );
};

export default ViewFeedByUrlPage;
